import { FormEvent, useState } from 'react';
import { Building2 } from 'lucide-react';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CreateTenantRequest } from '../models';
import { createTenant, getApiError } from '../services/api';

const initialValue: CreateTenantRequest = {
  tenantName: '', inn: '', address: '', administratorEmail: '',
  administratorPassword: '', administratorFirstName: '', administratorLastName: '',
  administratorMiddleName: '', administratorPhone: '', productName: 'Телемедицина',
  licenceSerial: '', licenceNumber: '', licenceIssuedAt: null, licenceExpiredAt: null,
  primaryColor: '#1976d2',
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (tenantName: string) => void;
}

export const CreateTenantDialog = ({ open, onClose, onCreated }: Props) => {
  const [value, setValue] = useState<CreateTenantRequest>(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field: keyof CreateTenantRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue((c) => ({ ...c, [field]: newValue }));
  };

  const close = () => { if (saving) return; setError(''); onClose(); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createTenant({
        ...value,
        licenceSerial: value.licenceSerial.trim(),
        licenceNumber: value.licenceNumber.trim(),
        licenceIssuedAt: value.licenceIssuedAt || null,
        licenceExpiredAt: value.licenceExpiredAt || null,
      });
      const name = value.tenantName;
      setValue(initialValue);
      onCreated(name);
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title="Новая организация" maxWidth="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>Отмена</Button>
          <Button type="submit" form="create-tenant-form" disabled={saving}>
            {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" /> : <Building2 className="h-4 w-4" />}
            Создать организацию
          </Button>
        </>
      }
    >
      <form id="create-tenant-form" onSubmit={submit} className="space-y-6">
        {error && <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div>
          <h3 className="mb-3 text-sm font-bold text-foreground">Организация</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input required label="Название медицинской организации" value={value.tenantName} onChange={update('tenantName')} />
            <Input required label="ИНН" value={value.inn} onChange={update('inn')} inputMode="numeric" />
            <Input required label="Адрес" value={value.address} onChange={update('address')} />
          </div>
        </div>

        <div className="border-t border-border" />

        <div>
          <h3 className="mb-1 text-sm font-bold text-foreground">Лицензия медицинской организации</h3>
          <p className="mb-3 text-xs text-muted-foreground">Заполните серию, номер и дату выдачи вместе. Дата окончания необязательна.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Серия лицензии" value={value.licenceSerial} onChange={update('licenceSerial')} maxLength={15} />
            <Input label="Номер лицензии" value={value.licenceNumber} onChange={update('licenceNumber')} maxLength={50} />
            <Input type="date" label="Дата выдачи" value={value.licenceIssuedAt ?? ''} onChange={update('licenceIssuedAt')} />
            <Input type="date" label="Дата окончания" value={value.licenceExpiredAt ?? ''} onChange={update('licenceExpiredAt')} />
          </div>
        </div>

        <div className="border-t border-border" />

        <div>
          <h3 className="mb-3 text-sm font-bold text-foreground">Администратор</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input required label="Фамилия" value={value.administratorLastName} onChange={update('administratorLastName')} />
            <Input required label="Имя" value={value.administratorFirstName} onChange={update('administratorFirstName')} />
            <Input label="Отчество" value={value.administratorMiddleName} onChange={update('administratorMiddleName')} />
            <Input required type="email" label="Email" value={value.administratorEmail} onChange={update('administratorEmail')} />
            <Input label="Телефон" value={value.administratorPhone} onChange={update('administratorPhone')} />
            <Input required type="password" label="Временный пароль" minLength={10} value={value.administratorPassword} onChange={update('administratorPassword')} />
          </div>
        </div>

        <div className="border-t border-border" />

        <div>
          <h3 className="mb-3 text-sm font-bold text-foreground">Брендинг</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Input required label="Название продукта" value={value.productName} onChange={update('productName')} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="create-primary-color" className="text-sm font-medium text-foreground">Основной цвет</label>
              <input id="create-primary-color" type="color" value={value.primaryColor} onChange={update('primaryColor')} className="h-10 w-full cursor-pointer rounded-lg border border-border bg-background" />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};
